import {createContext, useRef, useState} from 'react';

import {CarouselProviderProps, CarouselProviderValues} from './CarouselProvider.types';

const DEFAULT_VALUE = {
  ref: undefined,
  slideWidth: 0,
  goToNextSlide: () => {},
  goToPreviousSlide: () => {},
  setSlideWidth: () => {},
  setGap: () => {},
} as CarouselProviderValues;

export const CarouselProviderContext = createContext(DEFAULT_VALUE);

/**
 * CarouselProvider adds context values for controlling a Carousel within the Provider. You must set the ref on the Carousel's scrolling element, the slideWidth, and gap when using the related hook useCarousel. Once set you can use the goToNextSlide and goToPreviousSlide functions to control the Carousel. This allows for controls of the carousel to be placed wherever you want in the DOM.
 */
export const CarouselProvider = ({...otherProps}: CarouselProviderProps) => {
  const [slideWidth, setSlideWidth] = useState(0);
  const [gap, setGap] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const handleGoToNextSlide = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({left: slideWidth + gap, behavior: 'smooth'});
    }
  };

  const handleGoToPreviousSlide = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({left: -(slideWidth + gap), behavior: 'smooth'});
    }
  };

  return (
    <CarouselProviderContext.Provider
      value={{
        slideWidth,
        ref: carouselRef,
        goToNextSlide: handleGoToNextSlide,
        goToPreviousSlide: handleGoToPreviousSlide,
        setSlideWidth,
        setGap,
      }}
      {...otherProps}
    />
  );
};
